/* eslint-disable react/require-default-props */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Tooltip,
  Typography,
  styled
} from '@mui/material';
import useSWR from 'swr';
import Chart from 'react-apexcharts';
import dayjs from 'dayjs';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Api from '../../api';
import { getCurrency } from '../../utils/general';
import Loader from '../../components/shared/Loader';
import {
  successColors,
  alertColors,
  warningColors,
  secondaryColors
} from '../../theme/themeConfig';
import type { MetricItem } from '../../api/marginDashboard';

// ─── Status Color Map ──────────────────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  CRITICAL: alertColors.ALERT,
  OK: successColors.SUCCESS,
  WARNING: warningColors.ERROR
};

const getStatusColor = (status: string): string =>
  STATUS_COLORS[status?.toUpperCase()] || secondaryColors.SECONDARY;

// ─── Styled Components ─────────────────────────────────────────

const SectionCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  padding: theme.spacing(2.5)
}));

const MetricRow = styled(Box)(({ theme }) => ({
  '&:last-child': {
    borderBottom: 'none'
  },
  alignItems: 'center',
  borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
  display: 'flex',
  justifyContent: 'space-between',
  padding: `${theme.spacing(1.5)} 0`
}));

function StatusDot({ color }: { color: string }) {
  return (
    <Box
      sx={{
        backgroundColor: color,
        borderRadius: '50%',
        flexShrink: 0,
        height: 10,
        width: 10
      }}
    />
  );
}

// ─── Metric Display ────────────────────────────────────────────

function formatMetricValue(metric: MetricItem): string {
  if (metric.displayOverride) return metric.displayOverride;
  if (metric.value === null || metric.value === undefined) return '—';

  switch (metric.unit) {
    case '$':
      return getCurrency(metric.value);
    case '%':
      return `${metric.value}%`;
    case 'pts':
      return `${metric.value > 0 ? '+' : ''}${metric.value} pts`;
    case 'hrs':
      return `${metric.value > 0 ? '+' : ''}${metric.value} hrs`;
    case 'Days':
      return `${metric.value} days`;
    default:
      return `${metric.value}`;
  }
}

// ─── Tooltip Content ───────────────────────────────────────────

const METRIC_TOOLTIPS: Record<string, { body: string; title: string }> = {
  'A/R > 60 Days': {
    body: 'Total dollar amount of invoices that remain unpaid more than 60 days after issue. Aging receivables increase collection risk and reduce your available cash for operations.',
    title: 'Accounts Receivable Over 60 Days'
  },
  'Actual Gross Margin': {
    body: 'Your realized margin based on actual invoiced revenue minus actual costs incurred. Uses approved timesheets for labor and real travel data, with bid values for items not yet tracked (materials, supplies).',
    title: 'Actual Gross Margin'
  },
  'Contribution Margin': {
    body: 'The actual dollar profit from this project after subtracting all direct costs. This is revenue minus actual cost of service — the real dollars this job puts in your pocket.',
    title: 'Contribution Margin'
  },
  'Cost per Visit': {
    body: 'Your actual cost to deliver each visit (labor, travel, overhead) compared to what you bid. If actual exceeds bid, each visit is eating into your margin. Look at labor rate variance and unbudgeted hours to find the cause.',
    title: 'Cost per Visit'
  },
  DSO: {
    body: 'How many days on average it takes to collect payment after invoicing. A rising DSO means cash is tied up in receivables and may signal collection issues. Healthy is under 45 days.',
    title: 'Days Sales Outstanding'
  },
  Labor: {
    body: 'What portion of revenue goes to field labor. If this is climbing, you may be overstaffing visits, using higher-rate techs than planned, or seeing unbudgeted overtime. Industry benchmark is typically under 50%.',
    title: 'Labor as % of Revenue'
  },
  'Labor % of Revenue': {
    body: 'Total approved labor cost (including burden like taxes, insurance, workers comp) as a percentage of invoiced revenue. Rising values indicate labor is consuming more of each dollar earned.',
    title: 'Labor Cost Ratio'
  },
  'Labor Rate Variance': {
    body: 'How much more (or less) you are paying per hour compared to what was estimated in the bid. A +100% means you are paying double the planned rate. This catches expensive tech assignments that Unbudgeted Hours misses — same hours, but at a higher cost per hour.',
    title: 'Labor Rate Variance'
  },
  Margin: {
    body: 'How much of your revenue remains after subtracting direct job costs (labor, travel, materials, overhead). Compares your actual margin against what was bid. A drop signals cost overruns eating into profit.',
    title: 'Gross Margin'
  },
  'Margin Variance': {
    body: 'The gap between your actual and target margin in percentage points. Negative means you are underperforming your bid. Even a 2-3 point drop on a large contract can significantly impact profitability.',
    title: 'Margin Variance'
  },
  'Projected Rate Variance': {
    body: "Compares the assigned technician's hourly rate against the original bid rate BEFORE any work happens. If this is red, you have an expensive tech scheduled on a job that was priced for a cheaper one. Fix it now — once they clock in, the cost is locked.",
    title: 'Projected Rate Variance'
  },
  'Revenue per Labor Hour': {
    body: 'How much revenue each hour of field labor generates. Higher is better — it means your pricing supports your labor costs. If this drops, you are either underpricing jobs or overstaffing visits.',
    title: 'Revenue per Labor Hour'
  },
  'Revenue per Visit': {
    body: 'The prorated revenue earned per completed visit compared to the bid price per visit. If actual is below bid, you may be under-invoicing or giving away scope. If above, change orders or price adjustments are working in your favor.',
    title: 'Revenue per Visit'
  },
  'Supervisor Cost': {
    body: 'Total cost of supervisor visits in the period, calculated from visit duration × supervisor hourly rate. This is an overhead cost on top of field labor — factor it into your bid rates if supervisors are regularly assigned.',
    title: 'Supervisor Cost'
  },
  'Supervisor Rate Variance': {
    body: "Compares the supervisor's current hourly rate against the rate used when the job was bid. A positive number means you are paying more for supervision than the bid assumed — this directly reduces margin on every visit. If this is red, consider renegotiating the rate or updating the bid.",
    title: 'Supervisor Rate Variance'
  },
  'Target Gross Margin': {
    body: 'The margin percentage you expected when this project was bid. Derived from the proposal price minus all estimated costs including burden-loaded labor, travel, materials, and overhead.',
    title: 'Target Gross Margin'
  },
  'Unbudgeted Labor Hours': {
    body: 'The difference between actual approved hours worked and the hours originally scheduled. Positive means techs worked more than planned. This captures overtime, rework, and scope creep at the field level.',
    title: 'Unbudgeted Labor Hours'
  }
};

function getTooltipContent(label: string): string | null {
  if (METRIC_TOOLTIPS[label]) return METRIC_TOOLTIPS[label].body;
  const prefix = Object.keys(METRIC_TOOLTIPS).find((key) =>
    label.toLowerCase().startsWith(key.toLowerCase())
  );
  if (prefix) return METRIC_TOOLTIPS[prefix].body;
  return null;
}

function MetricLine({ metric }: { metric: MetricItem }) {
  const color = getStatusColor(metric.status);
  const tooltip = getTooltipContent(metric.label);

  return (
    <MetricRow>
      <Box
        alignItems="center"
        display="flex"
        gap={1.5}
        sx={{ flexShrink: 0, maxWidth: '55%' }}
      >
        <StatusDot color={color} />
        <Typography color="black" variant="heading_300">
          {metric.label}
        </Typography>
        {tooltip && (
          <Tooltip
            arrow
            componentsProps={{
              tooltip: {
                sx: { fontSize: 12, lineHeight: 1.5, maxWidth: 280 }
              }
            }}
            placement="top"
            title={tooltip}
          >
            <InfoOutlinedIcon
              sx={{
                color: secondaryColors.SECONDARY_3,
                cursor: 'help',
                fontSize: 16
              }}
            />
          </Tooltip>
        )}
      </Box>
      <Typography
        sx={{ color, fontSize: 14, textAlign: 'right' }}
        variant="primary-bold"
      >
        {formatMetricValue(metric)}
      </Typography>
    </MetricRow>
  );
}

// ─── Section Panel ─────────────────────────────────────────────

function SectionPanel({
  title,
  subtitle,
  metrics
}: {
  metrics: MetricItem[];
  subtitle: string;
  title: string;
}) {
  return (
    <SectionCard>
      <Box mb={1.5}>
        <Typography color="black" sx={{ fontSize: 15 }} variant="primary-bold">
          {title}
        </Typography>
        <Typography
          color={secondaryColors.SECONDARY_4}
          sx={{ fontSize: 12 }}
          variant="heading_300"
        >
          {subtitle}
        </Typography>
      </Box>
      {metrics.map((m) => (
        <MetricLine key={m.label} metric={m} />
      ))}
    </SectionCard>
  );
}

// ─── Gauge Chart ───────────────────────────────────────────────

function IntegrityGauge({ score }: { score: number }) {
  // Clamp to 0–100 for display
  const clampedScore = Math.max(0, Math.min(100, Math.round(score)));

  const getGaugeColor = () => {
    if (clampedScore >= 70) return successColors.SUCCESS;
    if (clampedScore >= 50) return warningColors.ERROR;
    return alertColors.ALERT;
  };

  const gaugeColor = getGaugeColor();

  return (
    <Box alignItems="center" display="flex" flexDirection="column">
      <Chart
        height={280}
        options={{
          chart: { type: 'radialBar' },
          colors: [gaugeColor],
          labels: ['Integrity Score'],
          plotOptions: {
            radialBar: {
              dataLabels: {
                name: {
                  color: secondaryColors.SECONDARY_4,
                  fontSize: '13px',
                  offsetY: -8,
                  show: true
                },
                value: {
                  color: gaugeColor,
                  fontSize: '32px',
                  fontWeight: 700,
                  formatter(val: number) {
                    return `${Math.round(val)}`;
                  },
                  offsetY: 4,
                  show: true
                }
              },
              hollow: { size: '60%' },
              track: {
                background:
                  clampedScore < 50 ? '#FDEAEA' : secondaryColors.SECONDARY_6,
                strokeWidth: '100%'
              }
            }
          },
          stroke: { lineCap: 'round' }
        }}
        series={[clampedScore]}
        type="radialBar"
        width={280}
      />
    </Box>
  );
}

// ─── Summary Card Status Colors ────────────────────────────────

const STATUS_BG: Record<string, string> = {
  CRITICAL: '#FDEAEA',
  OK: '#E8F9F3',
  WARNING: '#FEF6E7'
};

function deriveSummaryCardStatus(
  value: number,
  unit: string,
  benchmark: string
): string {
  if (unit === '%' && benchmark.toLowerCase().includes('below')) {
    return 'CRITICAL';
  }
  if (unit === '%' && benchmark.toLowerCase().includes('above normal')) {
    if (value > 60) return 'CRITICAL';
    return 'WARNING';
  }
  if (unit === 'Days') {
    if (value <= 45) return 'OK';
    if (value <= 60) return 'WARNING';
    return 'CRITICAL';
  }
  if (
    benchmark.toLowerCase().includes('healthy') ||
    benchmark.toLowerCase().includes('within') ||
    benchmark.toLowerCase().includes('at or above')
  ) {
    return 'OK';
  }
  return 'OK';
}

function SummaryCardItem({
  label,
  value,
  unit,
  benchmark,
  status
}: {
  benchmark: string;
  label: string;
  status?: string;
  unit: string;
  value: number;
}) {
  const displayValue = useMemo(() => {
    switch (unit) {
      case '$':
        return getCurrency(value);
      case '%':
        return `${value}%`;
      case 'Days':
        return `${value}`;
      default:
        return `${value}`;
    }
  }, [value, unit]);

  const resolvedStatus =
    status || deriveSummaryCardStatus(value, unit, benchmark);
  const statusColor = getStatusColor(resolvedStatus);
  const bgColor = STATUS_BG[resolvedStatus?.toUpperCase()] || '#FFFFFF';
  const tooltip = getTooltipContent(label);

  return (
    <div
      style={{
        backgroundColor: bgColor,
        borderLeft: `5px solid ${statusColor}`,
        borderRadius: '8px',
        boxShadow: '0px 2px 4px rgba(0,0,0,0.08)',
        padding: '24px',
        width: '100%'
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        gap={1}
        height="100%"
        justifyContent="center"
      >
        <Box alignItems="center" display="flex" gap={1}>
          <Typography color="black" variant="heading_300">
            {label}
          </Typography>
          {tooltip && (
            <Tooltip
              arrow
              componentsProps={{
                tooltip: {
                  sx: { fontSize: 12, lineHeight: 1.5, maxWidth: 280 }
                }
              }}
              placement="top"
              title={tooltip}
            >
              <InfoOutlinedIcon
                sx={{
                  color: secondaryColors.SECONDARY_3,
                  cursor: 'help',
                  fontSize: 16
                }}
              />
            </Tooltip>
          )}
        </Box>
        <Typography
          style={{ color: statusColor, fontSize: 28 }}
          variant="primary-bold"
        >
          {displayValue}
        </Typography>
        <Typography
          sx={{ color: secondaryColors.SECONDARY_4, fontSize: 11 }}
          variant="heading_300"
        >
          {benchmark}
        </Typography>
      </Box>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────

interface MarginDashboardProps {
  detailProjectId: string;
  projectEndDate?: string;
  projectStartDate?: string;
}

const MarginDashboard: React.FC<MarginDashboardProps> =
  function MarginDashboard({
    detailProjectId,
    projectStartDate,
    projectEndDate
  }) {
    // ── Project date boundaries ───────────────────────────────
    const projStart = projectStartDate
      ? dayjs(projectStartDate).format('YYYY-MM-DD')
      : null;
    const projEnd = projectEndDate
      ? dayjs(projectEndDate).format('YYYY-MM-DD')
      : null;

    // ── Period selector (default: project start → end of current month, clamped) ──
    const defaultStart =
      projStart || dayjs().startOf('month').format('YYYY-MM-DD');
    const defaultEnd =
      projEnd && dayjs().endOf('month').isAfter(dayjs(projEnd))
        ? projEnd
        : dayjs().endOf('month').format('YYYY-MM-DD');

    const [periodStart, setPeriodStart] = useState(defaultStart);
    const [periodEnd, setPeriodEnd] = useState(defaultEnd);

    // ── Data fetching (SWR — same pattern as Dashboard.tsx) ───
    const swrKey = detailProjectId
      ? {
          periodEnd,
          periodStart,
          projectId: detailProjectId,
          url: 'api/margin-dashboard'
        }
      : null;

    const { data, error, isValidating } = useSWR(
      swrKey,
      Api.MarginDashboardApi.getMarginDashboard
    );

    const isLoading = !data && !error && isValidating;

    // ── Loading state ─────────────────────────────────────────
    if (isLoading) {
      return (
        <Box
          alignItems="center"
          display="flex"
          justifyContent="center"
          minHeight={400}
        >
          <Loader customColor="primary" />
        </Box>
      );
    }

    // ── Error state ───────────────────────────────────────────
    if (error) {
      return (
        <Box
          alignItems="center"
          display="flex"
          justifyContent="center"
          minHeight={400}
        >
          <Typography color="error" variant="heading_300">
            Failed to load margin dashboard data
          </Typography>
        </Box>
      );
    }

    if (!data) return null;

    // ── Pre-start detection (from API flags) ────────────────
    const { isPreStart } = data;
    const hasPending = data.hasPendingHours;

    // ── Render ────────────────────────────────────────────────
    return (
      <Box display="flex" flexDirection="column" gap={3} width="100%">
        {/* Period Selector */}
        <Box alignItems="center" display="flex" flexWrap="wrap" gap={2}>
          <TextField
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: periodEnd,
              min: projStart || undefined
            }}
            label="Period Start"
            onChange={(e) => {
              let val = e.target.value;
              if (projStart && val < projStart) val = projStart;
              if (val > periodEnd) val = periodEnd;
              setPeriodStart(val);
            }}
            size="small"
            sx={{ width: 180 }}
            type="date"
            value={periodStart}
          />
          <TextField
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: projEnd || undefined,
              min: periodStart
            }}
            label="Period End"
            onChange={(e) => {
              let val = e.target.value;
              if (projEnd && val > projEnd) val = projEnd;
              if (val < periodStart) val = periodStart;
              setPeriodEnd(val);
            }}
            size="small"
            sx={{ width: 180 }}
            type="date"
            value={periodEnd}
          />
          {isValidating && <Loader customColor="primary" customSize={20} />}
        </Box>

        {/* Pre-start / Pending Banner */}
        {(isPreStart || hasPending) && (
          <Box
            style={{
              backgroundColor: '#FEF6E7',
              border: '1px solid #E8B931',
              borderRadius: '8px',
              padding: '12px 16px'
            }}
          >
            <Typography style={{ color: '#8B6914' }} variant="heading_300">
              {hasPending
                ? `Hours Pending Approval — ${data.pendingHours} hrs clocked but awaiting supervisor approval. Metrics will update once hours are approved and invoices are collected.`
                : 'Awaiting Execution — No paid revenue or approved labor recorded yet. Metrics below reflect bid targets only and are not actionable until work begins and invoices are collected.'}
            </Typography>
          </Box>
        )}

        {/* Partner Labor Banner */}
        {data.hasPartnerLabor && (
          <Box
            sx={{
              alignItems: 'flex-start',
              backgroundColor: '#E6F1FB',
              border: '1px solid #A2CDEC',
              borderRadius: '8px',
              display: 'flex',
              gap: 1.5,
              padding: '12px 16px'
            }}
          >
            <InfoOutlinedIcon
              sx={{ color: '#005DA2', fontSize: 18, mt: '2px' }}
            />
            <Box>
              <Typography
                sx={{ color: '#005DA2', fontSize: '13px', fontWeight: 600 }}
              >
                Partner labor included
              </Typography>
              <Typography sx={{ color: '#0f1c2e', fontSize: '12px', mt: 0.25 }}>
                This project includes subcontracted partner labor at a fixed
                contract rate. Partner costs are guaranteed per contract and
                excluded from variable labor calculations. SOW rates will appear
                here once configured.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Summary Cards */}
        <Grid container spacing={2}>
          {data.summaryCards.map((card) => (
            <Grid item key={card.label} sm={4} xs={12}>
              <SummaryCardItem
                benchmark={card.benchmark}
                label={card.label}
                status={card.status}
                unit={card.unit}
                value={card.value}
              />
            </Grid>
          ))}
        </Grid>

        {/* Gauge + Section Panels */}
        <Grid container spacing={2.5}>
          {/* Left column: Gauge */}
          <Grid item md={4} xs={12}>
            <SectionCard
              sx={{
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 340
              }}
            >
              <IntegrityGauge score={data.integrityScore.score} />
              <Box
                alignItems="center"
                display="flex"
                gap={0.5}
                justifyContent="center"
              >
                <Typography
                  sx={{
                    color: secondaryColors.SECONDARY_4,
                    fontSize: 12,
                    mt: -1,
                    textAlign: 'center'
                  }}
                  variant="heading_300"
                >
                  Composite of margin, labor, tasks &amp; cash health
                </Typography>
                <Tooltip
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: { fontSize: 12, lineHeight: 1.5, maxWidth: 280 }
                    }
                  }}
                  placement="top"
                  title="A weighted score (0-100) combining your margin health, labor efficiency, task completion, and cash collection. Scores above 70 are healthy. Below 50 signals multiple areas need attention."
                >
                  <InfoOutlinedIcon
                    sx={{
                      color: secondaryColors.SECONDARY_3,
                      cursor: 'help',
                      fontSize: 14,
                      mt: -1
                    }}
                  />
                </Tooltip>
              </Box>
            </SectionCard>
          </Grid>

          {/* Right column: Margin Integrity + Labor Pressure */}
          <Grid item md={8} xs={12}>
            <Grid container spacing={2.5}>
              <Grid item sm={6} xs={12}>
                <SectionPanel
                  metrics={[
                    data.marginIntegrity.targetGrossMargin,
                    data.marginIntegrity.actualGrossMargin,
                    data.marginIntegrity.marginVariance,
                    ...(data.marginIntegrity.contributionMargin
                      ? [data.marginIntegrity.contributionMargin]
                      : [])
                  ]}
                  subtitle={data.marginIntegrity.subtitle}
                  title={data.marginIntegrity.title}
                />
              </Grid>
              <Grid item sm={6} xs={12}>
                <SectionPanel
                  metrics={[
                    data.laborPressure.laborPercentOfRevenue,
                    data.laborPressure.unbudgetedLaborHours,
                    ...(data.laborPressure.revenuePerLaborHour
                      ? [data.laborPressure.revenuePerLaborHour]
                      : []),
                    ...(data.laborPressure.laborRateVariance
                      ? [data.laborPressure.laborRateVariance]
                      : []),
                    ...(data.laborPressure.projectedRateVariance
                      ? [data.laborPressure.projectedRateVariance]
                      : []),
                    ...(data.laborPressure.supervisorCost
                      ? [data.laborPressure.supervisorCost]
                      : []),
                    ...(data.laborPressure.supervisorRateVariance
                      ? [data.laborPressure.supervisorRateVariance]
                      : [])
                  ]}
                  subtitle={data.laborPressure.subtitle}
                  title={data.laborPressure.title}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Bottom row: Operational Leakage + Cash & Contract */}
          <Grid item sm={6} xs={12}>
            <SectionPanel
              metrics={[
                data.operationalLeakage.costPerVisit,
                data.operationalLeakage.revenuePerVisit
              ]}
              subtitle={data.operationalLeakage.subtitle}
              title={data.operationalLeakage.title}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <SectionPanel
              metrics={[
                data.cashContractHealth.arOver60Days,
                data.cashContractHealth.dsoDays
              ]}
              subtitle={data.cashContractHealth.subtitle}
              title={data.cashContractHealth.title}
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

export default MarginDashboard;
